/**
 * Settings shell: the left sub-nav and whichever pane the route names.
 *
 * The active pane is part of the URL (`/settings/team`), so a particular
 * setting can be linked to and the back button behaves.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { SETTINGS_NAV } from '../../constants/defaults.js';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { Card } from '../../components/Primitives.jsx';

import ProfilePane from './ProfilePane.jsx';
import OrgPane from './OrgPane.jsx';
import TeamPane from './TeamPane.jsx';
import DepartmentsPane from './DepartmentsPane.jsx';
import NotificationsPane from './NotificationsPane.jsx';
import SecurityPane from './SecurityPane.jsx';
import ListsPane from './ListsPane.jsx';
import SalesPane from './SalesPane.jsx';
import RulesPane from './RulesPane.jsx';
import AlertsPane from './AlertsPane.jsx';
import AutomationPane from './AutomationPane.jsx';
import AIPane from './AIPane.jsx';
import ColumnsPane from './ColumnsPane.jsx';
import AppearancePane from './AppearancePane.jsx';
import DataPane from './DataPane.jsx';
import AdvancedPane from './AdvancedPane.jsx';

const PANES = {
  profile: ProfilePane,
  org: OrgPane,
  team: TeamPane,
  depts: DepartmentsPane,
  notif: NotificationsPane,
  sec: SecurityPane,
  lists: ListsPane,
  sales: SalesPane,
  rules: RulesPane,
  alerts: AlertsPane,
  auto: AutomationPane,
  ai: AIPane,
  columns: ColumnsPane,
  appearance: AppearancePane,
  data: DataPane,
  adv: AdvancedPane
};

/*
 * What somebody who does not own the workspace may open here.
 *
 * Everything else on this screen changes the workspace for everybody in it
 * - the stage list, the roles, the automation, the price book. Their own
 * account and how the app looks to them are theirs; the rest is not.
 */
const OWN_PANES = ['profile', 'appearance'];

export default function Settings() {
  const { pane } = useParams();
  const navigate = useNavigate();
  const { resetSettings, me } = useApp();

  const owner = !me || me.role === 'Owner';

  /* The same fence in both directions: the rail only offers these, and
     typing the address of another one lands on Profile rather than on a
     pane the server will refuse to save. */
  const allowed = k => owner || OWN_PANES.includes(k);
  const active = PANES[pane] && allowed(pane) ? pane : (owner ? 'lists' : 'profile');
  const Pane = PANES[active];

  const nav = owner
    ? SETTINGS_NAV
    : SETTINGS_NAV
      .map(([group, items]) => [group, items.filter(([k]) => allowed(k))])
      .filter(([, items]) => items.length);

  const restoreDefaults = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Restore every setting to default? Clients are not affected.')) resetSettings();
  };

  return (
    <Card>
      <div className="setwrap">
        <div className="setnav">
          {nav.map(([group, items]) => (
            <div key={group}>
              <div className="sg">{group}</div>
              {items.map(([key, label, icon]) => (
                <button
                  type="button"
                  key={key}
                  className={active === key ? 'on' : ''}
                  onClick={() => navigate(`/settings/${key}`)}
                >
                  <Icon name={icon} />{label}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="setmain">
          <div className="pane on"><Pane /></div>
        </div>
      </div>

      {/* Resetting throws away the whole workspace's settings, not this
          person's, so it belongs to whoever owns it. */}
      {owner ? (
        <div className="sfoot">
          <button type="button" className="btn danger" onClick={restoreDefaults}>
            Restore default settings
          </button>
          <div />
        </div>
      ) : null}
    </Card>
  );
}
